'use client'

import { Icon } from "@/components/ui/icon"
import { useNotifications } from "@/lib/services/notifications/use-notifications"
import { Button, ButtonProps, cn, useOverlayState } from "@heroui/react"
import { useEffect, useState } from "react"
import { NotificationsPopover } from "./NotificationsPopover"

// Define a type for the notification with string type
interface NotificationWithStringType {
  id: string;
  type: string;
  read: boolean;
  [key: string]: any;
}

// Define all interaction notification types
const INTERACTION_TYPES = [
  'INTERACTION',
  'INTERACTION_FAVOURITE',
  'INTERACTION_COMMENT',
  'INTERACTION_RATING',
  'INTERACTION_SHARE',
  'INTERACTION_MENTION',
  'INTERACTION_DOWNLOAD',
  'INTERACTION_PURCHASE',
  'INTERACTION_VIEW'
];

export function NotificationBadge(props: ButtonProps) {
  // Use useOverlayState hook to manage popover state
  const { isOpen, open, close, setOpen } = useOverlayState();
  const { unreadCount, notifications, loading, error } = useNotifications()
  const [animateBadge, setAnimateBadge] = useState(false)

  // Cast notifications to the correct type - safely handle empty arrays
  const typedNotifications = (notifications || []) as unknown as NotificationWithStringType[]

  // Calculate unread counts by type - with error handling
  const unreadSystemCount = typedNotifications.filter(n => n && !n.read &&
    (n.type === 'SYSTEM' ||
     n.type === 'SECURITY' ||
     n.type === 'ALERT')).length

  // Updated to include new interaction types
  const unreadInteractionCount = typedNotifications.filter(n => n && !n.read &&
    (INTERACTION_TYPES.some(type => n.type === type) || n.type.startsWith('INTERACTION_')))
    .length;

  const unreadMessageCount = typedNotifications.filter(n => n && !n.read &&
    n.type === 'MESSAGE').length

  // Determine badge color based on notification types. Use static class names
  // (not `bg-${color}`) so Tailwind's JIT scanner can see and keep them.
  // Foreground follows each fill's own `-foreground` token: the accent fill
  // is teal in the v3 scope, which carries white text (teal pairing rule),
  // and danger/success stay white per their own tokens.
  let badgeBgClass = "bg-success"
  let badgeFgClass = "text-success-foreground"
  if (unreadSystemCount > 0) {
    badgeBgClass = "bg-danger"
    badgeFgClass = "text-danger-foreground"
  } else if (unreadInteractionCount > 0) {
    badgeBgClass = "bg-danger" // For favorites and other interactions
    badgeFgClass = "text-danger-foreground"
  } else if (unreadMessageCount > 0) {
    badgeBgClass = "bg-accent"
    badgeFgClass = "text-accent-foreground"
  }

  // Calculate total unread count to ensure it matches the server count
  const totalUnreadCount = unreadSystemCount + unreadInteractionCount + unreadMessageCount

  // Use the server-provided unreadCount, but fallback to calculated count if needed
  const displayCount = unreadCount > 0 ? unreadCount : totalUnreadCount

  // Animate badge when new notifications arrive
  useEffect(() => {
    if (displayCount > 0) {
      setAnimateBadge(true)
      const timer = setTimeout(() => setAnimateBadge(false), 2000)
      return () => clearTimeout(timer)
    }
  }, [displayCount])

  // // Debug logging - only in development
  // useEffect(() => {
  //   if (process.env.NODE_ENV === 'development') {
  //     console.log('NotificationBadge state:', {
  //       isOpen,
  //       unreadCount,
  //       totalUnreadCount,
  //       displayCount,
  //       unreadSystemCount,
  //       unreadInteractionCount,
  //       unreadMessageCount,
  //       notificationsCount: notifications?.length || 0,
  //       hasError: !!error,
  //     });
  //   }

  //   if (error) {
  //     console.error('Notification error:', error);
  //   }
  // }, [isOpen, unreadCount, totalUnreadCount, displayCount, unreadSystemCount,
  //     unreadInteractionCount, unreadMessageCount, notifications, error]);

  return (
    <NotificationsPopover
      isOpen={isOpen}
      onClose={close}
      onOpenChange={setOpen}
      hasError={!!error}
    >
      <Button
        variant="ghost"
        isIconOnly
        onPress={open}
        {...props}
        className={cn("relative overflow-visible", props.className)}
      >
        <Icon icon="notification" className={cn(
          "w-5 h-5 transition-transform duration-200",
          "group-hover:scale-110 overflow-auto",
        )} />
        {displayCount > 0 && (
          <div
            aria-hidden="true"
            className={cn(
              "flex absolute top-0 -right-1 justify-center items-center z-10 overflow-visible",
              "min-w-4 h-4 px-1 text-[9px] font-bold rounded-full",
              badgeBgClass,
              badgeFgClass,
              animateBadge ? "animate-bounce" : "animate-in zoom-in duration-200",
              "group-hover:scale-110 transition-transform"
            )}
          >
            {displayCount}
          </div>
        )}
      </Button>
    </NotificationsPopover>
  )
}
